/**
 * GET `/roster/issued_roster` — 이 훅의 응답 타입은 `@vTypes/snapshot-type`의 {@link IssuedRosterSnapshotResponse} 와 동일.
 *
 * - 성공 **200**: 서비스 dict를 그대로 JSON (별도 response_model 없음). `get_issued_roster_snapshot_service` 반환값.
 * - `meta` / `config`: DB `*_json`. `nurses`: nurses_json, `roster`: year·month·days_in_month·shift_colors·nurses(schedule: `{ code, color }[]`, schedule_ids, counts) 등.
 * - 오류: **401** 미인증 · **404** Issued snapshot not found · **500** 기타.
 * - 쿼리: `year`, `month` 필수. **마스터 관리자만** `group_id` 지정, 그 외는 세션 group_id.
 */

import useStore from "@store/store";
import { useQuery } from "@tanstack/react-query";
import httpClient from "@/config/axios";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import type { IssuedRosterSnapshotResponse } from "@vTypes/snapshot-type";

export type { IssuedRosterSnapshotResponse };

export default function useSnapShot({
  year,
  month,
  /** 비우면 세션 `loginUser.group_id` — URL 등에서 넘긴 병동으로 스냅샷 조회 */
  groupId,
}: {
  year?: number | string;
  month?: number | string;
  groupId?: string | null;
} = {}) {
  const { loginUser } = useStore();
  const normalizedYear = year ? Number(year) : undefined;
  const normalizedMonth = month ? Number(month) : undefined;
  const effectiveGroupId = (() => {
    const fromProp = (groupId ?? "").trim();
    if (fromProp !== "") return fromProp;
    return (loginUser?.group_id ?? "").trim();
  })();

  return useQuery<IssuedRosterSnapshotResponse | null>({
    queryKey: QUERY_KEYS.roster.issuedRosterSnapshot(
      normalizedYear as number,
      normalizedMonth as number,
      effectiveGroupId
    ),
    queryFn: async () => {
      const y = normalizedYear as number;
      const m = normalizedMonth as number;
      return await httpClient.get<IssuedRosterSnapshotResponse>(
        "/roster/issued_roster",
        {
          params: {
            year: y,
            month: m,
            group_id: effectiveGroupId,
          },
        }
      );
    },
    enabled:
      !!loginUser &&
      !!normalizedYear &&
      !!normalizedMonth &&
      effectiveGroupId !== "",
    staleTime: Infinity,
  });
}
